<script>
    import CursorTrail from "$lib/components/CursorTrail.svelte";
    import CelestialSky from "$lib/components/CelestialSky.svelte";
</script>

<svelte:head>
    <title>dinglandia | Design & Development Process</title>
    <meta
        name="description"
        content="The technical process here is a 4 step process that yields high end results. Design & Development are intertwined as we do rapid prototyping in the browser."
    />

    <meta property="og:title" content="Design & Development Process" />
    <meta property="og:site_name" content="dinglandia" />
    <meta property="og:url" content="https://dinglandia.design/process" />
    <meta
        property="og:description"
        content="The technical process here is a 4 step process that yields high end results. Design & Development are intertwined as we do rapid prototyping in the browser."
    />
    <meta property="og:type" content="business.business" />
    <meta
        property="og:image"
        content="https://dinglandia.design/og/process.jpg"
    />
</svelte:head>

<!-- Intro -->
<section id="intro" class="intro" data-trail="wrapper">
    <div class="icon-new" name="logo"></div>
    <h1 class="flash">process</h1>

    <!-- Cursor Trail Images -->
    <CursorTrail />
</section>
<!-- //Intro -->

<!-- Down Arrow -->
<a href="#process" aria-label="Jump to process details">
    <svg class="icon icon-arrow down-arrow"
        ><use xlink:href="#icon-arrow"></use></svg
    >
</a>
<!-- //Down Arrow -->

<!-- Process -->
<section id="process" class="process">
    <div class="container">
        <div class="row">
            <div class="col-md-6 offset-md-3 process__column">
                <h2 class="process__eyebrow">Our Technical Process</h2>
                <p class="process__lede">
                    Design and dev live together here&mdash;each phase stacks
                    like storm clouds until the big strike of launch. Open a
                    phase to watch the next one pulse to life.
                </p>

                <div class="process-timeline">
                    <div class="process-timeline__bolt" aria-hidden="true">
                        <span class="process-timeline__glow"></span>
                    </div>

                    <div class="process-timeline__steps process-accordion">
                        <details class="process-step process-step--discover">
                            <summary>
                                <span
                                    class="process-step__icon-splash"
                                    aria-hidden="true"
                                >
                                    <svg
                                        class="process-step__icon"
                                        viewBox="0 0 32 32"
                                        role="presentation"
                                        focusable="false"
                                    >
                                        <use xlink:href="#icon-discover"></use>
                                    </svg>
                                </span>
                                <span
                                    class="process-step__cloud"
                                    aria-hidden="true"
                                >
                                    <svg
                                        viewBox="0 0 120 80"
                                        role="presentation"
                                        focusable="false"
                                    >
                                        <path
                                            d="M91 31.5c-.53-11.87-10.11-21.36-22.07-21.36-4.1 0-7.97 1.06-11.32 2.94C57.01 6.83 49.15 1.5 40 1.5 25 1.5 12.86 13.63 12.86 28.63c0 .69.03 1.38.08 2.06C5.55 33.42 0 41.45 0 50.69 0 63.42 10.31 73.73 23.04 73.73H91c15 0 27.17-12.16 27.17-27.17 0-15.01-12.17-27.06-27.17-27.06Z"
                                        ></path>
                                    </svg>
                                    <span class="process-step__index">01</span>
                                </span>
                                <span class="process-step__heading">
                                    <span class="process-step__eyebrow"
                                        >Phase 01</span
                                    >
                                    <h3 class="process-step__title">
                                        Discover
                                    </h3>
                                    <span class="process-step__subtitle"
                                        >Proposal, alignment, and deep research.</span
                                    >
                                </span>
                                <svg
                                    class="process-step__toggle"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                    focusable="false"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M12 3.75a.75.75 0 0 1 .75.75v6.75h6.75a.75.75 0 0 1 0 1.5h-6.75v6.75a.75.75 0 0 1-1.5 0v-6.75H4.5a.75.75 0 0 1 0-1.5h6.75V4.5a.75.75 0 0 1 .75-.75Z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </summary>
                            <div class="process-step__body">
                                <p>
                                    From kickoff to competitor tear-downs we map
                                    the problem space, sign the scope, and
                                    surface every constraint before pushing a
                                    single pixel.
                                </p>
                                <ul class="process-step__list">
                                    <li>
                                        Proposal & agreement with measurable
                                        outcomes and success metrics.
                                    </li>
                                    <li>
                                        Research sprints covering:
                                        <ul class="process-step__sublist">
                                            <li>
                                                Industry landscape and behavior
                                                shifts
                                            </li>
                                            <li>
                                                Competitors & signature
                                                interactions worth beating
                                            </li>
                                            <li>
                                                Technical stack + required
                                                integrations
                                            </li>
                                            <li>
                                                3rd-party tooling, compliance,
                                                and data contracts
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </div>
                        </details>

                        <details class="process-step process-step--prototyping">
                            <summary>
                                <span
                                    class="process-step__icon-splash process-step__icon-splash--duo"
                                    aria-hidden="true"
                                >
                                    <svg
                                        class="process-step__icon"
                                        viewBox="0 0 32 32"
                                        role="presentation"
                                        focusable="false"
                                    >
                                        <use xlink:href="#icon-design"></use>
                                    </svg>
                                    <svg
                                        class="process-step__icon process-step__icon--secondary"
                                        viewBox="0 0 32 32"
                                        role="presentation"
                                        focusable="false"
                                    >
                                        <use xlink:href="#icon-develop"></use>
                                    </svg>
                                </span>
                                <span
                                    class="process-step__cloud"
                                    aria-hidden="true"
                                >
                                    <svg
                                        viewBox="0 0 120 80"
                                        role="presentation"
                                        focusable="false"
                                    >
                                        <path
                                            d="M91 31.5c-.53-11.87-10.11-21.36-22.07-21.36-4.1 0-7.97 1.06-11.32 2.94C57.01 6.83 49.15 1.5 40 1.5 25 1.5 12.86 13.63 12.86 28.63c0 .69.03 1.38.08 2.06C5.55 33.42 0 41.45 0 50.69 0 63.42 10.31 73.73 23.04 73.73H91c15 0 27.17-12.16 27.17-27.17 0-15.01-12.17-27.06-27.17-27.06Z"
                                        ></path>
                                    </svg>
                                    <span class="process-step__index">02</span>
                                </span>
                                <span class="process-step__heading">
                                    <span class="process-step__eyebrow"
                                        >Phase 02</span
                                    >
                                    <h3 class="process-step__title">
                                        Design + Development
                                    </h3>
                                    <span class="process-step__subtitle"
                                        >Rapid prototyping directly in the
                                        browser.</span
                                    >
                                </span>
                                <svg
                                    class="process-step__toggle"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                    focusable="false"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M12 3.75a.75.75 0 0 1 .75.75v6.75h6.75a.75.75 0 0 1 0 1.5h-6.75v6.75a.75.75 0 0 1-1.5 0v-6.75H4.5a.75.75 0 0 1 0-1.5h6.75V4.5a.75.75 0 0 1 .75-.75Z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </summary>
                            <div class="process-step__body">
                                <p>
                                    Design intuition meets code execution. We
                                    sketch systems with real components so
                                    decisions stick and the handoff is already
                                    production-ready.
                                </p>
                                <ul class="process-step__list">
                                    <li>
                                        Brand direction, UI inventory, and
                                        micro-interactions built directly in the
                                        browser.
                                    </li>
                                    <li>
                                        JAMstack prototypes with framework
                                        parity:
                                        <ul class="process-step__sublist">
                                            <li>React</li>
                                            <li>Vue</li>
                                        </ul>
                                    </li>
                                    <li>
                                        Data modeling and content architecture
                                        in headless CMS platforms:
                                        <ul class="process-step__sublist">
                                            <li>Contentful</li>
                                            <li>WordPress</li>
                                            <li>Drupal</li>
                                            <li>Sanity</li>
                                        </ul>
                                    </li>
                                    <li>
                                        Media pipelines tuned for CDNs:
                                        <ul class="process-step__sublist">
                                            <li>Cloudinary</li>
                                            <li>IMGIX</li>
                                            <li>AWS</li>
                                        </ul>
                                    </li>
                                </ul>
                            </div>
                        </details>

                        <details class="process-step process-step--devops">
                            <summary>
                                <span
                                    class="process-step__icon-splash"
                                    aria-hidden="true"
                                >
                                    <svg
                                        class="process-step__icon"
                                        viewBox="0 0 32 32"
                                        role="presentation"
                                        focusable="false"
                                    >
                                        <use xlink:href="#icon-devops"></use>
                                    </svg>
                                </span>
                                <span
                                    class="process-step__cloud"
                                    aria-hidden="true"
                                >
                                    <svg
                                        viewBox="0 0 120 80"
                                        role="presentation"
                                        focusable="false"
                                    >
                                        <path
                                            d="M91 31.5c-.53-11.87-10.11-21.36-22.07-21.36-4.1 0-7.97 1.06-11.32 2.94C57.01 6.83 49.15 1.5 40 1.5 25 1.5 12.86 13.63 12.86 28.63c0 .69.03 1.38.08 2.06C5.55 33.42 0 41.45 0 50.69 0 63.42 10.31 73.73 23.04 73.73H91c15 0 27.17-12.16 27.17-27.17 0-15.01-12.17-27.06-27.17-27.06Z"
                                        ></path>
                                    </svg>
                                    <span class="process-step__index">03</span>
                                </span>
                                <span class="process-step__heading">
                                    <span class="process-step__eyebrow"
                                        >Phase 03</span
                                    >
                                    <h3 class="process-step__title">DevOps</h3>
                                    <span class="process-step__subtitle"
                                        >Calm launches and resilient systems.</span
                                    >
                                </span>
                                <svg
                                    class="process-step__toggle"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                    focusable="false"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M12 3.75a.75.75 0 0 1 .75.75v6.75h6.75a.75.75 0 0 1 0 1.5h-6.75v6.75a.75.75 0 0 1-1.5 0v-6.75H4.5a.75.75 0 0 1 0-1.5h6.75V4.5a.75.75 0 0 1 .75-.75Z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </summary>
                            <div class="process-step__body">
                                <p>
                                    Monitoring, automation, and real-device QA
                                    keep the experience polished while we tee up
                                    launch protocols.
                                </p>
                                <ul class="process-step__list">
                                    <li>
                                        Browser and device testing on real
                                        hardware (no emulator surprises).
                                    </li>
                                    <li>
                                        Launch automation + hosting partners:
                                        <ul class="process-step__sublist">
                                            <li>Netlify</li>
                                            <li>Cloudflare</li>
                                        </ul>
                                    </li>
                                    <li>
                                        Performance optimization, accessibility
                                        budgets, and regression alerts.
                                    </li>
                                    <li>
                                        Analytics, reporting, and automation
                                        hand-offs for the wider team.
                                    </li>
                                </ul>
                            </div>
                        </details>

                        <details class="process-step process-step--marketing">
                            <summary>
                                <span
                                    class="process-step__icon-splash"
                                    aria-hidden="true"
                                >
                                    <svg
                                        class="process-step__icon"
                                        viewBox="0 0 32 32"
                                        role="presentation"
                                        focusable="false"
                                    >
                                        <use xlink:href="#icon-marketing"></use>
                                    </svg>
                                </span>
                                <span
                                    class="process-step__cloud"
                                    aria-hidden="true"
                                >
                                    <svg
                                        viewBox="0 0 120 80"
                                        role="presentation"
                                        focusable="false"
                                    >
                                        <path
                                            d="M91 31.5c-.53-11.87-10.11-21.36-22.07-21.36-4.1 0-7.97 1.06-11.32 2.94C57.01 6.83 49.15 1.5 40 1.5 25 1.5 12.86 13.63 12.86 28.63c0 .69.03 1.38.08 2.06C5.55 33.42 0 41.45 0 50.69 0 63.42 10.31 73.73 23.04 73.73H91c15 0 27.17-12.16 27.17-27.17 0-15.01-12.17-27.06-27.17-27.06Z"
                                        ></path>
                                    </svg>
                                    <span class="process-step__index">04</span>
                                </span>
                                <span class="process-step__heading">
                                    <span class="process-step__eyebrow"
                                        >Phase 04</span
                                    >
                                    <h3 class="process-step__title">
                                        Marketing & Advertisement
                                    </h3>
                                    <span class="process-step__subtitle"
                                        >Keep momentum with smart campaigns.</span
                                    >
                                </span>
                                <svg
                                    class="process-step__toggle"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                    focusable="false"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M12 3.75a.75.75 0 0 1 .75.75v6.75h6.75a.75.75 0 0 1 0 1.5h-6.75v6.75a.75.75 0 0 1-1.5 0v-6.75H4.5a.75.75 0 0 1 0-1.5h6.75V4.5a.75.75 0 0 1 .75-.75Z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </summary>
                            <div class="process-step__body">
                                <p>
                                    The new build gets a proper launch plan:
                                    content, paid support, and social loops to
                                    keep people coming back.
                                </p>
                                <ul class="process-step__list">
                                    <li>
                                        Email marketing sequences and
                                        automation.
                                    </li>
                                    <li>
                                        SEO roadmaps so fresh content actually
                                        ranks.
                                    </li>
                                    <li>
                                        SEM ads + retargeting for quick wins.
                                    </li>
                                    <li>SMM kits sized for every platform.</li>
                                    <li>
                                        Experiment-driven landing pages and
                                        personalization.
                                    </li>
                                </ul>
                            </div>
                        </details>

                        <details class="process-step process-step--profits">
                            <summary>
                                <span
                                    class="process-step__icon-splash"
                                    aria-hidden="true"
                                >
                                    <svg
                                        class="process-step__icon"
                                        viewBox="0 0 32 32"
                                        role="presentation"
                                        focusable="false"
                                    >
                                        <use xlink:href="#icon-profit"></use>
                                    </svg>
                                </span>
                                <span
                                    class="process-step__cloud"
                                    aria-hidden="true"
                                >
                                    <svg
                                        viewBox="0 0 120 80"
                                        role="presentation"
                                        focusable="false"
                                    >
                                        <path
                                            d="M91 31.5c-.53-11.87-10.11-21.36-22.07-21.36-4.1 0-7.97 1.06-11.32 2.94C57.01 6.83 49.15 1.5 40 1.5 25 1.5 12.86 13.63 12.86 28.63c0 .69.03 1.38.08 2.06C5.55 33.42 0 41.45 0 50.69 0 63.42 10.31 73.73 23.04 73.73H91c15 0 27.17-12.16 27.17-27.17 0-15.01-12.17-27.06-27.17-27.06Z"
                                        ></path>
                                    </svg>
                                    <span class="process-step__index">05</span>
                                </span>
                                <span class="process-step__heading">
                                    <span class="process-step__eyebrow"
                                        >Phase 05</span
                                    >
                                    <span class="process-step__title"
                                        >Value</span
                                    >
                                    <span class="process-step__subtitle"
                                        >Measure, learn, reinvest.</span
                                    >
                                </span>
                                <svg
                                    class="process-step__toggle"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                    focusable="false"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M12 3.75a.75.75 0 0 1 .75.75v6.75h6.75a.75.75 0 0 1 0 1.5h-6.75v6.75a.75.75 0 0 1-1.5 0v-6.75H4.5a.75.75 0 0 1 0-1.5h6.75V4.5a.75.75 0 0 1 .75-.75Z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </summary>
                            <div class="process-step__body">
                                <p>
                                    The lightning strike is revenue. We capture
                                    the signal, document the playbook, and queue
                                    what&rsquo;s next.
                                </p>
                                <ul class="process-step__list">
                                    <li>
                                        Live KPI dashboards tied to analytics +
                                        finance.
                                    </li>
                                    <li>
                                        Training, documentation, and async
                                        walkthroughs for your team.
                                    </li>
                                    <li>
                                        Backlog of optimizations for future
                                        sprints.
                                    </li>
                                    <li>
                                        On-demand experiments to keep profits
                                        compounding.
                                    </li>
                                </ul>
                            </div>
                        </details>
                    </div>
                </div>

                <p class="process__cta-note">
                    Ready to fast-forward through the clouds? I have a playbook
                    for that.
                </p>

                <div class="d-grid gap-2 col-6 mx-auto">
                    <a
                        href="/contact"
                        class="btn btn-lg btn-custom"
                        type="button">get in touch</a
                    >
                </div>
            </div>
        </div>
    </div>
</section>
<!-- //Process -->

<!-- New Mountain Parallax -->
<main class="content-grid">
    <section class="breakout">
        <CelestialSky />
        <div
            class="frame frame-square parallax-y"
            role="img"
            aria-label="Multi-layered scene depicting an underwater scene of some fish and Manta Ray. Set within the outline of a Manta Ray."
            style="--multiplier: 4"
        >
            <img src="/img/mountains/mtnBlu5.svg" alt="" style="--offset:0.1" />
            <img
                src="/img/mountains/mtnBlu4.svg"
                alt=""
                style="--offset:0.075"
            />
            <img
                src="/img/mountains/mtnBlue3.svg"
                alt=""
                style="--offset:0.05"
            />
            <img
                src="/img/mountains/mtnBlu2_fix.svg"
                alt=""
                style="--offset:0.025"
            />
            <img src="/img/mountains/mtnBlue.svg" alt="" style="--offset:0.012" />
            <div class="mountain-base"></div>
        </div>
        <p class="supports"></p>
    </section>
</main>
<!-- .//New Mountain Parallax -->

<!-- Services -->
<section id="services" class="services">
    <div class="container">
        <h2 class="text-center">What We Do</h2>
        <div class="services-grid">
            <a href="/services#v-pills-discover">
                <svg class="icon icon-discover"
                    ><use xlink:href="#icon-discover"></use></svg
                >
                <p>Discover</p>
            </a>
            <a href="/services#v-pills-design">
                <svg class="icon icon-design"
                    ><use xlink:href="#icon-design"></use></svg
                >
                <p>Design</p>
            </a>
            <a href="/services#v-pills-develop">
                <svg class="icon icon-develop"
                    ><use xlink:href="#icon-develop"></use></svg
                >
                <p>Develop</p>
            </a>
            <a href="/services#v-pills-devops">
                <svg class="icon icon-devops"
                    ><use xlink:href="#icon-devops"></use></svg
                >
                <p>DevOps</p>
            </a>
            <a href="/services#v-pills-agentic">
                <svg class="icon icon-robot"
                    ><use xlink:href="#icon-robot"></use></svg
                >
                <p>Agentic</p>
            </a>
        </div>

        <div class="row">
            <div class="col-md-6 offset-md-3">
                <div class="d-grid gap-2 col-6 mx-auto">
                    <a href="/work" class="btn btn-lg btn-custom" type="button"
                        >view work</a
                    >
                </div>
            </div>
        </div>
    </div>
</section>
<!-- //Services -->

<!-- Water Ripple CTA -->
<section class="ripple-cta">
    <div class="water-container">
        <canvas id="pool" aria-hidden="true"></canvas>
    </div>

    <div class="viking-ship-container">
        <svg
            class="viking-ship"
            viewBox="0 0 200 100"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Viking ship sailing"
        >
            <!-- Ship Hull -->
            <path
                d="M 20 50 Q 10 60, 20 70 L 180 70 Q 190 60, 180 50 Z"
                fill="#4a3426"
                stroke="#2a1f1a"
                stroke-width="2"
            />

            <!-- Hull Details -->
            <rect
                x="25"
                y="55"
                width="150"
                height="3"
                fill="#3a2920"
                opacity="0.7"
            />
            <rect
                x="25"
                y="62"
                width="150"
                height="2"
                fill="#3a2920"
                opacity="0.5"
            />

            <!-- Mast -->
            <rect x="98" y="15" width="4" height="45" fill="#2a1f1a" />

            <!-- Sail -->
            <path
                d="M 45 20 Q 100 25, 155 20 L 150 45 Q 100 48, 50 45 Z"
                fill="#e8d5c4"
                stroke="#2a1f1a"
                stroke-width="1.5"
                opacity="0.95"
            />

            <!-- Sail Stripes -->
            <rect
                x="50"
                y="25"
                width="100"
                height="4"
                fill="#b84c4c"
                opacity="0.8"
            />
            <rect
                x="50"
                y="33"
                width="100"
                height="4"
                fill="#b84c4c"
                opacity="0.8"
            />
            <rect
                x="50"
                y="41"
                width="100"
                height="3"
                fill="#b84c4c"
                opacity="0.8"
            />

            <!-- Shield decorations -->
            <circle
                cx="40"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="60"
                cy="60"
                r="6"
                fill="#4a7299"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="80"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="100"
                cy="60"
                r="6"
                fill="#4a7299"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="120"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="140"
                cy="60"
                r="6"
                fill="#4a7299"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="160"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />

            <!-- Dragon head -->
            <path
                d="M 180 50 Q 195 45, 197 50 L 195 55 Q 190 52, 180 52 Z"
                fill="#4a3426"
                stroke="#2a1f1a"
                stroke-width="1.5"
            />

            <!-- Oars (simplified) -->
            <line
                x1="35"
                y1="68"
                x2="28"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="55"
                y1="68"
                x2="48"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="75"
                y1="68"
                x2="68"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="125"
                y1="68"
                x2="132"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="145"
                y1="68"
                x2="152"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="165"
                y1="68"
                x2="172"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
        </svg>
    </div>

    <div class="trees trees--cta" aria-hidden="true"></div>

    <div class="overlay" aria-hidden="true">
        <div class="overlay-inner">
            <h2 class="title">We take you from point A to point B.</h2>
            <h3 class="subtitle">And everywhere in-between.</h3>
            <div class="cta">
                Let's create custom web experiences for your customers!
            </div>
            <div class="logo-tag">
                <button
                    type="button"
                    class="btn btn-lg btn-custom"
                    data-fillout-id="n2vCxyr9Huus"
                    data-fillout-embed-type="popup"
                    data-fillout-dynamic-resize
                    data-fillout-inherit-parameters
                    data-fillout-popup-size="small">navigate the winds</button
                >
            </div>
        </div>
    </div>

    <!-- <div class="scroll-indicator">↓ scroll to sail ↓</div> -->

    <div class="fallback" hidden aria-hidden="true">
        WebGL2 is required to view this water effect.<br />
        Please try again in a modern browser.
    </div>
</section>
<!-- Water Ripple CTA -->
